import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { withStyles, makeStyles, useTheme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import Skeleton from '@material-ui/lab/Skeleton';
// import { CircularProgress } from '@material-ui/core';

import WarningIcon from '@material-ui/icons/Error';
import CompletedCountIcon from '../../../assets/icons/CompletedCountIcon';
import OverdueCountIcon from '../../../assets/icons/OverdueCountIcon';
import PendingIcon from '../../../assets/icons/PendingCountIcon';
import CancelIcon from '../../../assets/icons/CancelIcon';
import RequestIcon from '../../../assets/icons/RequestIcon';
import BookedIcon from '../../../assets/icons/BookedIcon';
import OverdueIcon from '../../../assets/icons/OverdueIcon';
import CompletedIcon from '../../../assets/icons/CompletedIcon';

import HeaderCell from './HeaderCell';
import TableButton from './TableButton';
import Checkbox from '../Checkbox';
import Progress from '../Progress';

import { userUtils, setsUtils, dateUtils } from '../../../utils';

import { enums, casesConstants } from '../../../constants';
import userRoles, { multiTenantRoles } from '../../../constants/userRoles';
import { countStatuses } from '../../../constants/enums';

import consignmentIcon from '../../../assets/consignment_icon.svg';
import loanIcon from '../../../assets/loan_icon.svg';
import CheckIcon from '../../../assets/icons/CheckIcon';
import ReceiptIcon from '../../../assets/icons/ReceiptIcon';

import './table.scss';

const { caseStatusOptions, setAllocationStatuses, kitVariantTypes } = enums;
const { statusColors } = casesConstants;

const StyledTableCell = withStyles(theme => ({
  root: {
    marginTop: '16px',
  },
  head: {
    backgroundColor: 'transparent',
    color: theme.palette.text.disabled,
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingTop: props => props.small ? '8px' : '16px',
    paddingBottom: props => props.small ? '8px' : '16px',
    textTransform: 'uppercase',
    // fontSize: '0.8rem',
  },
  body: {
    color: 'var(--app-text) !important',
    borderTop: 'none',
    borderBottom: 'none',
    backgroundColor: props => props.background,
    paddingTop: props => props.small ? '8px' : '16px',
    paddingBottom: props => props.small ? '8px' : '16px',
    verticalAlign: 'middle',
    // fontSize: '0.8rem',
    // fontWeight: 'bold',
    '&:first-child': {
      borderRadius: props => props.checkboxes ? '0' : '24px 0 0 24px'
    },
    '&:last-child': {
      borderRadius: '0 24px 24px 0'
    }
  },
}))(TableCell);

const StyledTableRow = withStyles(theme => ({
  root: {
    // fontWeight: 'bold',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    '&:last-child': {}
  },
}))(TableRow);

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3),
    overflowX: 'auto',
    boxShadow: 'none'
  },
  table: {
    minWidth: 300,
    marginTop: '16px',
    position: 'relative',
    borderCollapse: 'separate',
    borderSpacing: '0 10px'
  },
  checkboxActive: {
    paddingLeft: '16px',
    paddingTop: '12px',
    paddingBottom: '12px',
    backgroundColor: 'var(--app-table-row-selected)',
    border: 'none',
    borderRadius: '24px 0 0 24px',
    verticalAlign: 'middle'
  },
  checkbox: {
    paddingLeft: '16px',
    paddingTop: '12px',
    paddingBottom: '12px',
    backgroundColor: 'var(--app-table-row)',
    border: 'none',
    borderRadius: '24px 0 0 24px',
    verticalAlign: 'middle'
  },
  checkboxHead: {
    paddingLeft: '16px',
    paddingTop: '4px',
    paddingBottom: '0px',
  }
}));

const renderCell = (column, row, additionalData = {}) => {
  const type = column.type;
  const value = row[column.field];
  const { tenantColor } = additionalData;

  switch (type) {
    case 'status':
      return !!value ? 'Active' : 'Deactivated';
    case 'role':
      return userRoles[value] ? userRoles[value].label : '';
    case 'date':
      return value ? dateUtils.formatDate(value) : '';
    case 'caseStatus':
      return <div className='d-flex'>
        {!!value.status && (
          <div className='m-r-md' style={{ marginTop: 4 }}>{getStatusIcon(value.status, value.canceled, value.proformaSent, value.orderClosed)}</div>
        )}
        {!!value.kitVariant && value.status !== caseStatusOptions.request && (
          value.kitVariant === kitVariantTypes.consignment && !!value.additional ? (
            <div className="d-flex">
              <div className="case-variant-icon">{getVariantIcon(kitVariantTypes.consignment)}</div>
              <div className="m-l-md case-variant-icon">{getVariantIcon(kitVariantTypes.loan)}</div>
            </div>
          ) : (
            <div className="case-variant-icon">{getVariantIcon(value.kitVariant)}</div>
          )
        )}
      </div>;
    case 'userStatus':
      const { active, role, connected } = row;
      const { surgeons = [] } = additionalData;

      if (!active) {
        return 'Deactivated';
      }

      if ([...multiTenantRoles].includes(role) || connected) {
        if (userUtils.isAssigned(row, surgeons)) {
          return 'Assigned';
        } else {
          return (<div className='d-flex' style={{ color: statusColors.REQUEST }}>
            <span className='m-r-lg'>Review</span>
            <WarningIcon style={{ color: statusColors.REQUEST }} />
          </div>);
        }
      }

      return 'Active';
    case 'setPosition':
      return setsUtils.getPositionLabel(value);
    case 'setAllocationStatus':
      return setsUtils.getSetAllocationStatus(value);
    case 'setsAllocation':
      return row.status === setAllocationStatuses.AVAILABLE.value && (!row.sets || !row.sets.length) ? (
        <span style={{ color: setAllocationStatuses.RETURNED.color }}>Assign Set</span>
      ) : (
        <div>{value.map((set) => <div key={set}>{set}</div>)}</div>
      );
    case 'caseId':
      const { onClick } = additionalData;
      return (
        <span
          style={{ color: tenantColor }}
          onClick={() => onClick(value)}
        >
          {value}
        </span>
      );
    case 'scans':
      const tags = additionalData.tags || [];
      const tag = row.tag ? tags?.find((t) => t.id === row.tag) : null;
      return value && Array.isArray(value) ? (
        <div>
          {tag && (
            <div style={{ color: tag.color || '#000000', marginBottom: '6px' }}>
              {tag.name}
            </div>
          )}
          <div className='d-flex'>
            {['GTIN', 'REF', 'LOT', 'EXP', 'DESC'].map((key) => {
              const scan = value?.find((v) => v.label === key);
              if (scan && scan.value) {
                return (
                  <div key={`${scan.label}_${scan.value}`} className='m-r-lg'>
                    <div className='font-bold secondary m-b-sm'>{scan.label}</div>
                    <div className='font-bold'>{scan.value}</div>
                  </div>
                )
              }
              return null;
            })}
          </div>
        </div>
      ) : null;
    case 'colored':
      return <div style={{ color: value.color }}>{value.value}</div>;
    case 'progress':
      return <Progress value={value.value} variant='determinate' bgcolor={value.color} />;
    case 'warning':
      return !!value && <WarningIcon style={{ color: statusColors.REQUEST }} />;
    case 'error':
      return !!value && <WarningIcon style={{ color: statusColors.OVERDUE }} />;
    case 'list':
      return Array.isArray(value) && (
        <div className='d-flex direction-column align-start'>
          {value.map((item) => (
            <div key={Math.random() * Math.random()} className='m-b-sm'>
              {item}
            </div>
          ))}
        </div>
      );
    case 'action':
      if (value && value.onClick) {
        return (
          <TableButton
            onClick={value.onClick}
            // color={tenantColor}
          >
            {value.icon}
          </TableButton>
        );
      } else {
        return null;
      }
    case 'action-primary':
      if (value && value.onClick) {
        return (
          <TableButton
            onClick={value.onClick}
            color={tenantColor}
          >
            {value.icon}
          </TableButton>
        );
      } else {
        return null;
      }
    case 'countStatus':
      return (
        <div className='d-flex'>
          {getCountStatusIcon(value)}
          {row.kit && (
            <div className='case-variant-icon'>
              <img
                className='m-l-md'
                src={consignmentIcon}
                alt='consignment_icon'
              />
            </div>
          )}
        </div>
      );
    case 'custom':
      return column.formatter ? column.formatter(value, row) : value;
    default:
      return value;
  }
};

const getStatusIcon = (status, canceled, proformaSent, orderClosed) => {
  if (canceled) {
    return <CancelIcon fontSize='small' color={statusColors.OVERDUE} />;
  }

  switch (status) {
    case caseStatusOptions.request:
      return <RequestIcon fontSize='small' color={statusColors.REQUEST} />;
    case caseStatusOptions.booked:
      return <BookedIcon fontSize='small' color={statusColors.BOOKED} />;
    case caseStatusOptions.overdue:
      return <OverdueIcon fontSize='small' color={statusColors.OVERDUE} />;
    case caseStatusOptions.completed:
      if (proformaSent) {
        if (orderClosed) {
          return <CheckIcon fontSize='small' color={statusColors.COMPLETED} />
        } else {
          return <ReceiptIcon fontSize='small' color={statusColors.COMPLETED}  />
        }
      }
      return <CompletedIcon fontSize='small' color={statusColors.COMPLETED} />;
    default:
      return <RequestIcon fontSize='small' color={statusColors.REQUEST} />;
  }
};

const getCountStatusIcon = (status) => {
  switch (status) {
    case countStatuses.PENDING.value:
      return <PendingIcon color={countStatuses.PENDING.color} />;
    case countStatuses.COMPLETED.value:
      return <CompletedCountIcon color={countStatuses.COMPLETED.color} />;
    case countStatuses.OVERDUE.value:
      return <OverdueCountIcon color={countStatuses.OVERDUE.color} />;
    default:
      return null;
  }
};

const getVariantIcon = (variant) => {
  switch (variant) {
    case kitVariantTypes.consignment:
      return (
        <img
          src={consignmentIcon}
          alt='consignment_icon'
        />
      );
    case kitVariantTypes.loan:
      return (
        <img
          src={loanIcon}
          alt='loan_icon'
        />
      );
    default:
      return null;
  }
};

const TableRemoteComponent = (props) => {
  const {
    columns,
    rows,
    onRowClick,
    selected,
    handleCheck,
    handleCheckAll,
    checkedRows,
    additionalData,
    rowColor = 'var(--app-table-row)',
    rowBorderColor = 'transparent',
    pagination,
    checkboxes = true,
    noCheckAll = false,
    className,
    onFilterChange = () => {},
    currentFilter = {},
    filterOptions = {},
    order,
    setOrder,
    orderBy,
    setOrderBy,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalRows,
    loading,
    variant
  } = props;

  const classes = useStyles();
  const theme = useTheme();

  const tenantColor = useSelector((state) => state.tenant.currentTenant.colorPrimary);
  const legacySurfaceColors = ['#ffffff', '#fff', '#fafafa'];
  const legacyGapColors = ['#ffffff', '#fff', '#fafafa', '#f3f3f4'];
  const normalizedRowColor = theme.palette.type === 'dark' && legacySurfaceColors.includes(String(rowColor).toLowerCase())
    ? 'var(--app-table-row)'
    : rowColor;
  const normalizedRowBorderColor = theme.palette.type === 'dark' && legacyGapColors.includes(String(rowBorderColor).toLowerCase())
    ? 'transparent'
    : rowBorderColor;
  const selectedRowColor = 'var(--app-table-row-selected)';

  // const prevRows = usePrevious(rows);

  // useEffect(() => {
  //   if (rows && prevRows && rows.length !== prevRows.length) {
  //     setPage(0);
  //   }
  // }, [rows]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Table
      className={`app-spaced-table ${classes.table}${className ? ` ${className}` : ''}`}
      aria-label='customized table'
      style={{
        '--app-table-gap-bg': normalizedRowBorderColor,
        '--app-table-row-gap': loading ? '4px' : '10px'
      }}
    >
      {/*{loading && (*/}
      {/*  <div*/}
      {/*    className='d-flex p-lg flex-center align-center'*/}
      {/*    style={{*/}
      {/*      position: 'absolute',*/}
      {/*      height: '100%',*/}
      {/*      width: '100%',*/}
      {/*      background: 'rgba(227, 230, 233, 0.52)',*/}
      {/*      zIndex: 1000,*/}
      {/*      borderRadius: '8px'*/}
      {/*  }}*/}
      {/*  >*/}
      {/*    <CircularProgress size={40} />*/}
      {/*  </div>*/}
      {/*)}*/}
      <TableHead>
        <TableRow>
          {checkboxes && (
            <th className={classes.checkboxHead} style={{ width: '45px' }}>
              {!noCheckAll && (
                <Checkbox
                  input={{
                    checked: !!rows.length && rows?.every((row) => checkedRows.includes(row.id)),
                    onClick: handleCheckAll
                  }}
                />
              )}
            </th>
          )}
          {columns.map(column => (
            <HeaderCell
              key={`header-cell-${column.title}-${Math.random()}`}
              field={column.field}
              title={column.title}
              sort={column.sort}
              order={order}
              orderBy={orderBy}
              onSort={handleSort}
              filterOptions={column.filter && filterOptions[column.field] ? filterOptions[column.field] : null}
              selectedValues={column.filter && currentFilter[column.field] ? currentFilter[column.field] : []}
              onFilterChange={(values) => onFilterChange(column.field, values)}
              filterTitle={column.filterTitle}
              small={variant === 'small'}
            />
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          (new Array(rowsPerPage || 10).fill(null)).map((_, i) => (
            <TableRow key={i}>
              <TableCell
                className='app-table-skeleton-cell'
                colSpan={checkboxes ? columns.length + 1 : columns.length}
                style={{
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingLeft: 8,
                  paddingRight: 8,
                  border: 'none'
                }}
              >
                <Skeleton
                  animation="wave"
                  height={90}
                  style={{ margin: '-12px 0' }}
                />
              </TableCell>
            </TableRow>
          ))
        ) : (
          rows.map(row => (
            <StyledTableRow key={row.id}>
              {checkboxes && (
                <td
                  className={`app-table-checkbox-cell ${row.id === selected ? classes.checkboxActive : classes.checkbox}`}
                  style={{
                    '--app-table-cell-bg': row.id === selected ? selectedRowColor : normalizedRowColor,
                    ...(variant === 'small' ? { paddingTop: '4px', paddingBottom: '4px' } : {})
                  }}
                >
                  <Checkbox
                    input={{
                      onClick: () => handleCheck(row.id),
                      checked: checkedRows?.indexOf(row.id) !== -1
                    }}
                    fontSize={variant === 'small' ? 'small' : 'medium'}
                  />
                </td>
              )}
              {columns.map(column =>
                <StyledTableCell
                  key={`cell-${column.title}_${row.id}_${Math.random()}`}
                  component='td'
                  scope='row'
                  background={row.id === selected ? selectedRowColor : normalizedRowColor}
                  style={{ '--app-table-cell-bg': row.id === selected ? selectedRowColor : normalizedRowColor }}
                  checkboxes={checkboxes}
                  onClick={() => onRowClick(row)}
                  small={variant === 'small'}
                >
                  {renderCell(column, row, { ...additionalData, tenantColor })}
                </StyledTableCell>)}
            </StyledTableRow>
          ))
        )}

        {/*{rows && rows.length ? (*/}
        {/*  rows.map(row => (*/}
        {/*    <StyledTableRow key={row.id} color={rowBorderColor}>*/}
        {/*      {checkboxes && (*/}
        {/*        <td*/}
        {/*          className={row.id === selected || rowColor === '#ffffff' ? classes.checkboxActive : classes.checkbox }*/}
        {/*          style={variant === 'small' ? { paddingTop: '4px', paddingBottom: '4px' } : {}}*/}
        {/*        >*/}
        {/*          <Checkbox*/}
        {/*            input={{*/}
        {/*              onClick: () => handleCheck(row.id),*/}
        {/*              checked: checkedRows?.indexOf(row.id) !== -1*/}
        {/*            }}*/}
        {/*            fontSize={variant === 'small' ? 'small' : 'medium'}*/}
        {/*          />*/}
        {/*        </td>*/}
        {/*      )}*/}
        {/*      {columns.map(column =>*/}
        {/*        <StyledTableCell*/}
        {/*          key={`cell-${column.title}_${row.id}_${Math.random()}`}*/}
        {/*          component='td'*/}
        {/*          scope='row'*/}
        {/*          background={row.id === selected ? '#ffffff' : rowColor}*/}
        {/*          checkboxes={checkboxes}*/}
        {/*          onClick={() => onRowClick(row)}*/}
        {/*          small={variant === 'small'}*/}
        {/*        >*/}
        {/*          {renderCell(column, row, { ...additionalData, tenantColor })}*/}
        {/*        </StyledTableCell>)}*/}
        {/*    </StyledTableRow>*/}
        {/*  ))*/}
        {/*) : (*/}
        {/*  <tr className='p-lg'>*/}
        {/*    <td className='secondary text-center p-lg font-size-bg' colSpan={columns.length + 1}>*/}
        {/*      EMPTY LIST*/}
        {/*    </td>*/}
        {/*  </tr>*/}
        {/*)}*/}

        <tr className='app-table-pagination-row'>
          {pagination && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 20]}
              count={totalRows || rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </tr>
      </TableBody>
    </Table>
  );
};

TableRemoteComponent.propTypes = {
  columns: PropTypes.array,
  rows: PropTypes.array,
  currentFilter: PropTypes.object,
  filterOptions: PropTypes.object,
  onRowClick: PropTypes.func,
  onFilterChange: PropTypes.func,
  stickyTable: PropTypes.bool,
  surgeons: PropTypes.array,
  checkboxes: PropTypes.bool,
};

TableRemoteComponent.defaultProps = {
  onRowClick: () => {},
  handleCheck: () => {},
  handleCheckAll: () => {},
  checkedRows: [],
  checkboxes: true,
};

export default memo(TableRemoteComponent);
